import { useState } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import IconButton from "@mui/material/IconButton";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import Toolbar from "@mui/material/Toolbar";
import { RoutePlanView } from "./RoutePlanView";
import { RoutePlanFooter } from "./RoutePlanFooter";
import { useTranslation } from "react-i18next";

export const DRAWER_WIDTH = 300;

interface RoutePlanDrawerProps {
  window?: () => Window;
  mobileOpen?: boolean;
  onMobileToggle?: (open: boolean) => void;
  desktopOpen?: boolean;
  onDesktopToggle?: (open: boolean) => void;
  hideDesktopToggleHandle?: boolean;
  hideMobileToggleHandle?: boolean;
  onStartDemo?: () => void;
}

export function RoutePlanDrawer({
  window,
  mobileOpen: controlledMobileOpen,
  onMobileToggle,
  desktopOpen: controlledDesktopOpen,
  onDesktopToggle,
  hideDesktopToggleHandle = false,
  hideMobileToggleHandle = false,
  onStartDemo,
}: RoutePlanDrawerProps) {
  const { t } = useTranslation();
  const [internalMobileOpen, setInternalMobileOpen] = useState(false);
  const [internalDesktopOpen, setInternalDesktopOpen] = useState(true);

  const mobileOpen =
    controlledMobileOpen !== undefined
      ? controlledMobileOpen
      : internalMobileOpen;

  // Use controlled state if provided, otherwise use internal state
  const desktopOpen =
    controlledDesktopOpen !== undefined
      ? controlledDesktopOpen
      : internalDesktopOpen;

  const handleDesktopToggle = () => {
    const newState = !desktopOpen;
    if (onDesktopToggle) {
      onDesktopToggle(newState);
    } else {
      setInternalDesktopOpen(newState);
    }
  };

  const handleMobileOpen = () => {
    if (onMobileToggle) {
      onMobileToggle(true);
    } else {
      setInternalMobileOpen(true);
    }
  };

  const handleMobileClose = () => {
    if (onMobileToggle) {
      onMobileToggle(false);
    } else {
      setInternalMobileOpen(false);
    }
  };

  const container =
    window !== undefined ? () => window().document.body : undefined;
  const drawerEasing = {
    enter: "cubic-bezier(0.22, 1, 0.36, 1)",
    exit: "cubic-bezier(0.4, 0, 0.2, 1)",
  };
  const drawerDuration = { enter: 260, exit: 220 };
  const widthTransition = `width ${
    desktopOpen ? drawerDuration.enter : drawerDuration.exit
  }ms ${desktopOpen ? drawerEasing.enter : drawerEasing.exit}`;
  const paperTransition = `transform ${
    desktopOpen ? drawerDuration.enter : drawerDuration.exit
  }ms ${desktopOpen ? drawerEasing.enter : drawerEasing.exit}, opacity ${
    desktopOpen ? 200 : 160
  }ms ${desktopOpen ? drawerEasing.enter : drawerEasing.exit}`;

  return (
    <>
      <Box
        component="nav"
        sx={{
          width: { md: desktopOpen ? DRAWER_WIDTH : 0 },
          flexShrink: { sm: 0 },
          transition: { md: widthTransition },
          willChange: "width",
        }}
        aria-label="route plan drawer"
      >
        {/* Mobile Swipeable Drawer */}
        <SwipeableDrawer
          container={container}
          disableDiscovery
          disableSwipeToOpen
          anchor="left"
          open={mobileOpen}
          onOpen={handleMobileOpen}
          onClose={handleMobileClose}
          transitionDuration={drawerDuration}
          SlideProps={{ easing: drawerEasing }}
          PaperProps={{ id: "route-plan-drawer" }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              bgcolor: "#FBF6F2",
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              touchAction: "pan-y",
              overflowX: "hidden",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Toolbar
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                minHeight: "48px !important",
                px: 1,
              }}
            >
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleMobileClose}
                aria-label={t("common.close")}
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                pb: 2,
              }}
            >
              <RoutePlanView />
            </Box>
            <RoutePlanFooter onStartDemo={onStartDemo} />
          </Box>
        </SwipeableDrawer>

        {/* Desktop Permanent Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              bgcolor: "#FBF6F2",
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              transition: paperTransition,
              transform: desktopOpen ? "translateX(0)" : "translateX(-100%)",
              opacity: desktopOpen ? 1 : 0,
              pointerEvents: desktopOpen ? "auto" : "none",
              willChange: "transform, opacity",
              overflowX: "hidden",
            },
          }}
          PaperProps={{ id: "route-plan-drawer" }}
          open={desktopOpen}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                pb: 2,
              }}
            >
              <RoutePlanView />
            </Box>
            <RoutePlanFooter onStartDemo={onStartDemo} />
          </Box>
        </Drawer>
      </Box>

      {/* Mobile Drawer Toggle Handle */}
      {!hideMobileToggleHandle && (
        <Box
          role="button"
          aria-label={t("drawer.openRoutePlan")}
          aria-controls="route-plan-drawer"
          onClick={handleMobileOpen}
          sx={{
            display: { xs: mobileOpen ? "none" : "flex", md: "none" },
            position: "fixed",
            top: "calc(50% + env(safe-area-inset-top))",
            left: 0,
            transform: "translateY(-50%)",
            zIndex: 900,
            background:
              "linear-gradient(90deg, rgba(17,24,39,0.92), rgba(17,24,39,0.75))",
            backdropFilter: "saturate(140%) blur(12px)",
            WebkitBackdropFilter: "saturate(140%) blur(12px)",
            color: "#fff",
            borderTopRightRadius: 16,
            borderBottomRightRadius: 16,
            border: "1px solid rgba(255,255,255,0.2)",
            boxShadow:
              "0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05) inset",
            padding: "12px 10px",
            alignItems: "center",
            gap: 0,
            cursor: "pointer",
            minHeight: 48,
            pr: 0.5,
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            userSelect: "none",
            WebkitTapHighlightColor: "transparent",
            "&:hover": {
              background:
                "linear-gradient(90deg, rgba(17,24,39,0.98), rgba(17,24,39,0.85))",
              boxShadow:
                "0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1) inset",
              transform: "translateY(-50%) translateX(2px)",
              borderColor: "rgba(255,255,255,0.3)",
            },
            "&:active": {
              transform: "translateY(-50%) translateX(1px)",
              boxShadow:
                "0 6px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05) inset",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              transition: "transform 0.2s ease",
              "&:hover": {
                transform: "scale(1.1)",
              },
            }}
          >
            <DragIndicatorIcon
              sx={{
                color: "rgba(255,255,255,0.95)",
                fontSize: 24,
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
              }}
            />
          </Box>
        </Box>
      )}

      {/* Desktop Toggle Handle */}
      {!hideDesktopToggleHandle && (
        <Box
          role="button"
          aria-label={
            desktopOpen ? t("drawer.hideRoutePlan") : t("drawer.showRoutePlan")
          }
          aria-controls="route-plan-drawer"
          onClick={handleDesktopToggle}
          sx={{
            display: { xs: "none", md: "flex" },
            position: "fixed",
            top: "calc(50% + env(safe-area-inset-top))",
            left: desktopOpen ? DRAWER_WIDTH : 0,
            transform: "translateY(-50%)",
            zIndex: 900,
            background: desktopOpen
              ? "linear-gradient(90deg, rgba(17,24,39,0.92), rgba(17,24,39,0.75))"
              : "linear-gradient(90deg, rgba(17,24,39,0.95), rgba(17,24,39,0.80))",
            backdropFilter: "saturate(140%) blur(12px)",
            WebkitBackdropFilter: "saturate(140%) blur(12px)",
            color: "#fff",
            borderTopRightRadius: 16,
            borderBottomRightRadius: 16,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            border: "1px solid rgba(255,255,255,0.2)",
            borderLeft: desktopOpen
              ? "none"
              : "1px solid rgba(255,255,255,0.2)",
            boxShadow: desktopOpen
              ? "0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05) inset"
              : "0 10px 36px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.08) inset",
            padding: desktopOpen ? "12px 10px" : "12px 14px 12px 10px",
            alignItems: "center",
            gap: 0,
            cursor: "pointer",
            minHeight: 48,
            minWidth: 48,
            pr: 0.5,
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            userSelect: "none",
            WebkitTapHighlightColor: "transparent",
            "&:hover": {
              background: desktopOpen
                ? "linear-gradient(90deg, rgba(17,24,39,0.98), rgba(17,24,39,0.85))"
                : "linear-gradient(90deg, rgba(17,24,39,1), rgba(17,24,39,0.90))",
              boxShadow: desktopOpen
                ? "0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1) inset"
                : "0 14px 44px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.12) inset",
              transform: desktopOpen
                ? "translateY(-50%) translateX(-2px)"
                : "translateY(-50%) translateX(2px)",
              borderColor: "rgba(255,255,255,0.3)",
            },
            "&:active": {
              transform: "translateY(-50%)",
              boxShadow:
                "0 6px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05) inset",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              transition: "transform 0.2s ease",
              transform: desktopOpen ? "rotate(180deg)" : "rotate(0deg)",
              "&:hover": {
                transform: desktopOpen
                  ? "rotate(180deg) scale(1.1)"
                  : "rotate(0deg) scale(1.1)",
              },
            }}
          >
            <DragIndicatorIcon
              sx={{
                color: "rgba(255,255,255,0.95)",
                fontSize: 24,
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
}
